import { useCallback, useState } from 'react'
import {
  faFileAlt as OrderDetailsIcon,
  faList as OrderCardsIcon,
} from '@fortawesome/free-solid-svg-icons'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'

import Currency from 'components/ui/Currency'
import TSTable from 'components/Tanstack'

import { useTableUtils } from 'hooks/useTableUtils'
import {
  OrdersWestfieldCorpDataTypes,
  OrdersWestfieldCorpModalActionsTypes,
} from 'types/orders-westfieldcorp'

export const useTablesOrders = ({
  dataTable,
}: {
  dataTable: OrdersWestfieldCorpDataTypes[]
}) => {
  const {
    selectedModal,
    selectedModalSet,
    selectedRowData,
    selectedRowDataSet,
  } = useTableUtils<
    OrdersWestfieldCorpDataTypes,
    OrdersWestfieldCorpModalActionsTypes
  >()

  const handleActionModalClose = useCallback(() => {
    selectedRowDataSet(null)
    selectedModalSet(null)
  }, [selectedModalSet, selectedRowDataSet])

  const handleViewOderDetailModalOpen = useCallback(
    (rowData: OrdersWestfieldCorpDataTypes) => {
      selectedRowDataSet(rowData)
      selectedModalSet('orders_details_preview')
    },
    [selectedRowDataSet, selectedModalSet]
  )

  const handleViewOderCardModalOpen = useCallback(
    (rowData: OrdersWestfieldCorpDataTypes) => {
      selectedRowDataSet(rowData)
      selectedModalSet('orders_cards_preview')
    },
    [selectedRowDataSet, selectedModalSet]
  )

  const columnHelper = createColumnHelper<OrdersWestfieldCorpDataTypes>()
  const columns = [
    columnHelper.accessor('orderId', {
      header: () => 'Order #',
      cell: (info) => {
        const orderId = info.getValue()

        return (
          <p className="flex flex-col">
            <span>{orderId}</span>
          </p>
        )
      },
      // footer: (props) => props.column.id,
    }),
    columnHelper.accessor('orderStatusName', {
      header: () => 'Status',
      cell: (info) => {
        const status = info.getValue()
        return (
          <p>
            <small className="uppercase">{status}</small>
          </p>
        )
      },
    }),
    columnHelper.accessor('totalTopupAmount', {
      header: () => 'Amount',

      cell: ({ row }) => {
        const { original } = row
        const { totalTopupAmount } = original
        const amount = totalTopupAmount

        return (
          <p className="font-bold">
            {amount.toLocaleString(undefined, {
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            })}
            <Currency countryCode={'NZ'} />
          </p>
        )
      },
    }),
    columnHelper.accessor('contactFirstName', {
      header: () => 'Contact',
      cell: ({ row }) => {
        const { original } = row
        const { contactFirstName, contactLastName } = original
        const contact = `${contactFirstName} ${contactLastName}`

        return (
          <p>
            <span>{contact}</span>
          </p>
        )
      },
    }),
    columnHelper.accessor('deliveryAddress1', {
      header: () => 'Delivery Details',
      cell: ({ row }) => {
        const { original } = row
        const {
          deliveryAddress1,
          deliveryAddress2,
          deliveryCity,
          deliverySuburb,
          deliveryState,
          deliveryPostcode,
          deliveryCompanyName,
        } = original
        const company = `${deliveryCompanyName} `
        const address = `${deliveryAddress1} \n ${deliveryAddress2 ?? ''} `
        const city = `\n ${deliverySuburb} \n ${deliveryState ?? ''} \n ${deliveryCity ?? ''} \n ${deliveryPostcode ?? ''}`

        return (
          <>
            <p>
              <b>{company}</b>
            </p>
            <p className="font-light">{address}</p>
            <p className="font-light">{city}</p>
          </>
        )
      },
    }),
    columnHelper.accessor('purchaseOrder', {
      header: () => 'Notes',
      cell: ({ row }) => {
        const { original } = row
        const { purchaseOrder } = original
        const purchaseOrderNotes = purchaseOrder

        return (
          <p className="font-light">
            <span>{purchaseOrderNotes}</span>
          </p>
        )
      },
    }),
    columnHelper.accessor('paymentReason', {
      header: () => 'Payment Details',
      cell: ({ row }) => {
        const { original } = row
        const { paymentReason, paymentTypeName } = original

        return (
          <>
            <p>
              <b>{paymentTypeName}</b>
            </p>
            <p className="font-light">{paymentReason}</p>
          </>
        )
      },
    }),
    columnHelper.accessor('prmCode', {
      header: () => 'Promo',
      cell: (info) => {
        const promo = info.getValue()
        return (
          <p className="font-light">
            <small>{promo}</small>
          </p>
        )
      },
    }),
    columnHelper.accessor('trackingNumber', {
      header: () => 'Tracking Details',
      cell: ({ row }) => {
        const { original } = row
        const { trackingNumber, trackingStatus } = original

        return (
          <>
            <p>
              <b>{trackingNumber}</b>
            </p>
            <p className="font-light">{trackingStatus}</p>
          </>
        )
      },
    }),

    columnHelper.accessor('invoiceDate', {
      header: () => 'Date',
      cell: (info) => info.getValue(),
      // footer: (props) => props.column.id,
    }),

    columnHelper.display({
      id: 'actions',
      header: () => '',
      // cell: (info) => info.getValue(),
      cell: ({ row }) => (
        <div className="flex gap-2 px-4">
          <TSTable.TableCellButton
            onClick={() => handleViewOderDetailModalOpen(row.original)}
            label="Details"
            icon={OrderDetailsIcon}
          />

          <TSTable.TableCellButton
            onClick={() => handleViewOderCardModalOpen(row.original)}
            label="Cards"
            icon={OrderCardsIcon}
          />
        </div>
      ),
    }),
  ] as ColumnDef<OrdersWestfieldCorpDataTypes>[]

  return {
    selectedRowData,
    selectedModal,
    dataTable,
    columns,
    handleActionModalClose,
  }
}
